import React from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  Chip,
  Avatar,
  Stack,
  Paper,
  Grid,
  Divider,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Email as EmailIcon,
  AdminPanelSettings as RoleIcon,
  Schedule as ScheduleIcon,
  Language as LanguageIcon,
  AttachMoney as CurrencyIcon,
  Star as PlanIcon,
  Verified as VerifiedIcon,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../auth/hooks/useAuth";

interface DetailRowProps {
  label: string;
  value: string | React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value }) => {
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ py: 1.5 }}
    >
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          fontWeight: 500,
          minWidth: 120,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: "text.primary",
          fontWeight: 500,
          textAlign: "right",
          flex: 1,
        }}
      >
        {value || "Not provided"}
      </Typography>
    </Stack>
  );
};

interface SectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, icon, children }) => {
  return (
    <Paper
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: 2,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
        <Box
          sx={{
            color: "primary.main",
            display: "flex",
            alignItems: "center",
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: "text.primary",
          }}
        >
          {title}
        </Typography>
      </Stack>
      {children}
    </Paper>
  );
};

export const ProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  if (!user) {
    return null;
  }

  const getUserInitials = () => {
    return `${user.first_name?.charAt(0) || ""}${
      user.last_name?.charAt(0) || ""
    }`.toUpperCase();
  };

  const formatRole = () => {
    return (
      user.role
        ?.replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (l) => l.toUpperCase()) || "User"
    );
  };

  const formatDate = (timestamp: number | string | Date | undefined) => {
    if (!timestamp) return "Not available";
    return new Date(timestamp).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getDaysRemaining = () => {
    if (!user.business?.subscription) return 0;
    const end = new Date(user.business.subscription.end_date).getTime();
    const now = Date.now();
    return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
  };

  return (
    <Box sx={{ bgcolor: "grey.50", minHeight: "100vh" }}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 4, py: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={3}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/app-launcher")}
                sx={{
                  borderColor: "primary.main",
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: "primary.main",
                    color: "white",
                  },
                }}
              >
                Back
              </Button>

              <Stack direction="row" alignItems="center" spacing={2}>
                <PersonIcon
                  sx={{
                    color: "primary.main",
                    fontSize: 28,
                  }}
                />
                <Box>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      fontSize: { xs: "1.75rem", md: "2.125rem" },
                    }}
                  >
                    Profile
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.875rem",
                    }}
                  >
                    Personal and business information
                  </Typography>
                </Box>
              </Stack>
            </Stack>
          </Stack>
        </motion.div>

        {/* Profile Header Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
        >
          <Paper
            variant="outlined"
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 2,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack direction="row" spacing={3} alignItems="center">
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: "primary.main",
                  fontSize: "2rem",
                  fontWeight: 600,
                  border: "3px solid",
                  borderColor: "background.paper",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
              >
                {getUserInitials()}
              </Avatar>

              <Box sx={{ flex: 1 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={2}
                  sx={{ mb: 1 }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                    }}
                  >
                    {user.first_name} {user.last_name}
                  </Typography>
                  {user.is_active && (
                    <VerifiedIcon
                      sx={{
                        color: "success.main",
                        fontSize: 24,
                      }}
                    />
                  )}
                </Stack>

                <Stack direction="row" spacing={1.5} alignItems="center">
                  <Chip
                    label={formatRole()}
                    variant="outlined"
                    size="small"
                    sx={{
                      borderColor: "primary.main",
                      color: "primary.main",
                      fontWeight: 500,
                    }}
                  />
                  {user.is_store_owner && (
                    <Chip
                      label="Store Owner"
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: "success.main",
                        color: "success.main",
                        fontWeight: 500,
                      }}
                    />
                  )}
                  {user.is_super_admin && (
                    <Chip
                      label="Super Admin"
                      variant="outlined"
                      size="small"
                      sx={{
                        borderColor: "warning.main",
                        color: "warning.main",
                        fontWeight: 500,
                      }}
                    />
                  )}
                </Stack>

                <Typography
                  variant="body2"
                  sx={{
                    color: "text.secondary",
                    mt: 1,
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <EmailIcon sx={{ fontSize: 16 }} />
                  {user.email}
                </Typography>
              </Box>
            </Stack>
          </Paper>
        </motion.div>

        <Grid container spacing={4}>
          {/* Personal Information */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              <Section title="Personal Information" icon={<PersonIcon />}>
                <Stack spacing={0}>
                  <DetailRow
                    label="Full Name"
                    value={`${user.first_name} ${user.last_name}`}
                  />
                  <Divider />
                  <DetailRow label="Email" value={user.email} />
                  <Divider />
                  <DetailRow label="Role" value={formatRole()} />
                  <Divider />
                  <DetailRow
                    label="Account Created"
                    value={formatDate(user.created_at)}
                  />
                  <Divider />
                  <DetailRow
                    label="Last Login"
                    value={formatDate(user.last_login_at)}
                  />
                </Stack>
              </Section>
            </motion.div>
          </Grid>

          {/* Business Information */}
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.3, ease: "easeOut" }}
            >
              <Section title="Business Information" icon={<BusinessIcon />}>
                <Stack spacing={0}>
                  <DetailRow
                    label="Business Name"
                    value={user.business?.business_name}
                  />
                  <Divider />
                  <DetailRow
                    label="Display ID"
                    value={
                      user.business?.display_id
                        ? `#${user.business.display_id}`
                        : "Not assigned"
                    }
                  />
                  <Divider />
                  <DetailRow
                    label="Language"
                    value={user.business?.settings?.language?.toUpperCase()}
                  />
                  <Divider />
                  <DetailRow
                    label="Currency"
                    value={user.business?.settings?.currency}
                  />
                  <Divider />
                  <DetailRow
                    label="Timezone"
                    value={user.business?.settings?.timezone}
                  />
                </Stack>
              </Section>
            </motion.div>
          </Grid>

          {/* Subscription Information */}
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
            >
              <Section title="Subscription Details" icon={<PlanIcon />}>
                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Stack spacing={0}>
                      <DetailRow
                        label="Plan"
                        value={
                          <Chip
                            label={
                              user.business?.subscription?.plan?.toUpperCase() ||
                              "Not specified"
                            }
                            variant="filled"
                            size="small"
                            sx={{
                              bgcolor: "primary.main",
                              color: "white",
                              fontWeight: 600,
                            }}
                          />
                        }
                      />
                      <Divider />
                      <DetailRow
                        label="Status"
                        value={
                          <Chip
                            label={
                              user.business?.subscription?.status || "Unknown"
                            }
                            variant="outlined"
                            size="small"
                            color={
                              user.business?.subscription?.status === "active"
                                ? "success"
                                : "error"
                            }
                            sx={{ fontWeight: 500 }}
                          />
                        }
                      />
                      <Divider />
                      <DetailRow
                        label="Days Remaining"
                        value={`${getDaysRemaining()} days`}
                      />
                      <Divider />
                      <DetailRow
                        label="Start Date"
                        value={
                          user.business?.subscription?.start_date
                            ? formatDate(user.business.subscription.start_date)
                            : "Not specified"
                        }
                      />
                      <Divider />
                      <DetailRow
                        label="End Date"
                        value={
                          user.business?.subscription?.end_date
                            ? formatDate(user.business.subscription.end_date)
                            : "Not specified"
                        }
                      />
                    </Stack>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Stack spacing={0}>
                      <DetailRow
                        label="Max Users"
                        value={
                          user.business?.subscription?.features?.max_users ||
                          "Not specified"
                        }
                      />
                      <Divider />
                      <DetailRow
                        label="Max Products"
                        value={
                          user.business?.subscription?.features?.max_products ||
                          "Not specified"
                        }
                      />
                      <Divider />
                      <DetailRow
                        label="Max Locations"
                        value={
                          user.business?.subscription?.features
                            ?.max_locations || "Not specified"
                        }
                      />
                      <Divider />
                      <DetailRow
                        label="Inventory Access"
                        value={
                          <Chip
                            label={
                              user.business?.subscription?.features
                                ?.has_inventory
                                ? "Enabled"
                                : "Disabled"
                            }
                            variant="outlined"
                            size="small"
                            color={
                              user.business?.subscription?.features
                                ?.has_inventory
                                ? "success"
                                : "default"
                            }
                            sx={{ fontWeight: 500 }}
                          />
                        }
                      />
                      <Divider />
                      <DetailRow
                        label="Analytics Access"
                        value={
                          <Chip
                            label={
                              user.business?.subscription?.features
                                ?.has_analytics
                                ? "Enabled"
                                : "Disabled"
                            }
                            variant="outlined"
                            size="small"
                            color={
                              user.business?.subscription?.features
                                ?.has_analytics
                                ? "success"
                                : "default"
                            }
                            sx={{ fontWeight: 500 }}
                          />
                        }
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Section>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
